<template>
    <template v-if="mode === 'interno'">
        <div v-if="isVideo">
            <video controls autoplay muted loop style="max-height:500px; width: 100%;">
                <source :src="pregunta.imagen" type="video/mp4" />
                Your browser does not support the video tag.
            </video>
        </div>
        <v-img v-else max-height="500" :src="pregunta.imagen" />
    </template>
    <v-card variant="text">
        <v-card-title class="text-wrap" style="word-break: break-word">
            {{ pregunta.texto }}
        </v-card-title>
        <v-card-text class="d-flex align-center justify-center flex-column">
            <v-rating v-model="rating" :length="sortedOptions.length" :density="sortedOptions.length > 10 ? 'compact' : 'default'"
                :item-labels="labelsOpciones" :full-icon="fullIcon" :empty-icon="emptyIcon" class="ma-2"
                item-label-position="top"></v-rating>
        </v-card-text>
    </v-card>
    <v-btn block v-if="!haRespondido && mode !== 'teacher'" :disabled="loading || !rating" color="primary" class="mt-3"
        @click="seleccionarOpcion">
        {{ t('Enviar') }}
    </v-btn>
    <v-btn block v-if="haRespondido && mode === 'interno'" :disabled="loading" color="primary" class="mt-3"
        @click="next">
        {{ t('Continua') }}
    </v-btn>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { axios } from '../axios';

const props = defineProps({
    pregunta: {
        type: Object,
        default: () => ({}),
    },
    mode: {
        type: String,
        default: 'interno',
        // 'interno'  => El componente hace el axios POST a enviar_respuestas/.
        // 'externo'  => El componente emite el evento con los datos, 
        //               y NO hace la petición dentro.
    },
    alumnoID: {
        default: null,
    },
    selectedOptionVista: {
        default: 'alumno',
    }
});

const emit = defineEmits(['respuesta-enviada', 'info-respuesta']);

const { t } = useI18n();
const respuestasSeleccionadas = ref([]);
const haRespondido = ref(false);
const loading = ref(false);

// Ref para el rating, el cual usaremos como valor "orden" seleccionado
const rating = ref(null);

const sortedOptions = computed(() => {
  return (props.pregunta.opciones || [])
    .filter(o => o && o.orden !== undefined)
    .sort((a, b) => a.orden - b.orden);
});

const labelsOpciones = computed(() => sortedOptions.value.map(o => o.texto2));

// Función auxiliar para validar el texto del icono
const obtenerTextoValido = (texto) => {
    if (
        typeof texto === 'string' &&
        texto.trim().length >= 3
    ) {
        return texto.trim()
    }
    return null
}

// Computed: Genera el ícono completo a partir de pregunta.opciones[0].texto o usa por defecto "mdi-star"
const fullIcon = computed(() => {
    const textoValido = props.pregunta?.opciones?.[0]?.texto;
    const validado = obtenerTextoValido(textoValido)
    if (validado) {
        return `mdi-${validado}`
    }
    return 'mdi-star'
})

// Computed: Genera el ícono vacío agregando "-outline" o usa por defecto "mdi-star-outline"
const emptyIcon = computed(() => {
    const textoValido = props.pregunta?.opciones?.[0]?.texto;
    const validado = obtenerTextoValido(textoValido)
    if (validado) {
        return `mdi-${validado}-outline`
    }
    return 'mdi-star-outline'
})

function seleccionarOpcion() {
    const opcion = sortedOptions.value.find(option => option.orden === rating.value);
    console.log('sortedOptions.value:', sortedOptions.value)
    console.log('rating.value:', rating.value)
    console.log('opcion:', opcion)
    console.log('labelsOpciones:', labelsOpciones.value)
    if (opcion) {
        respuestasSeleccionadas.value = [opcion.id];
        // enviamos un evento con la info de la respuesta para el "modo externo"
        // (o si el padre quisiera usarlo incluso en modo interno).
        emit('info-respuesta', {
            opcion_respuesta_id: opcion.id,
            pregunta_id: props.pregunta.id,
        });

        if (props.mode === 'interno') {
            // Lógica interna de envío
            enviarAlServidor(opcion.id);
        } else {
            // Modo externo: no hace axios.post. Marcamos "respondido" localmente
            haRespondido.value = true;
        }
    }

}

async function enviarAlServidor(opcionId) {
    const datosParaEnviar = {
        selectedOptionVista: props.selectedOptionVista,
        alumnoID: props.alumnoID,
        opcion_respuesta_ids: [opcionId],
    };
    loading.value = true;
    console.log('enviando:', datosParaEnviar)
    try {
        const respuesta = await axios.post(`enviar_respuestas/`, datosParaEnviar);
        if (respuesta.data.success) {
            haRespondido.value = true;
        } else {
            console.error('Error al enviar respuestas:', respuesta.data.message);
        }
    } catch (error) {
        console.error('Error al realizar la solicitud:', error);
    } finally {
        loading.value = false;
        if (props.selectedOptionVista === 'docente') {
            next();
        }
    }
}

function next() {
    // Emitir el evento que indica que la pregunta ha sido contestada y pasamos a la siguiente.
    emit('respuesta-enviada', props.pregunta);
    respuestasSeleccionadas.value = [];
}

// Reiniciamos el estado cada vez que la pregunta cambie
watch(
    () => props.pregunta?.id,
    (newId, oldId) => {
        if (newId !== oldId) {
            haRespondido.value = false;
            respuestasSeleccionadas.value = [];
        }
    }
);

// Check if the file is a video (mp4)
const isVideo = computed(() => {
    if (props.pregunta.value && props.pregunta.value.imagen) {
        try {
            const urlObj = new URL(props.pregunta.value.imagen);
            return urlObj.pathname.toLowerCase().endsWith('.mp4');
        } catch (e) {
            // Fallback if URL parsing fails
            return props.pregunta.value.imagen.toLowerCase().includes('.mp4');
        }
    }
    return false;
});

// Inicializar las respuestas seleccionadas con respuestas previas del usuario
onMounted(() => {
    if (props.pregunta.respuestas_usuario && props.pregunta.respuestas_usuario.length > 0) {
        respuestasSeleccionadas.value = props.pregunta.respuestas_usuario.map(
            (respuesta) => respuesta.opcion_respuesta.id
        );
    }
});
</script>